import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import { Edit, Person, School, Sports, VideoLibrary } from '@mui/icons-material'
import { TRACK_EVENTS, isHttpUrl } from '@tnf-recruit/shared'
import { useStudentProfile } from '../../../hooks/useStudentProfile'
import { ProfileSection } from '../ProfileSection'
import { ProfileField } from '../ProfileField'
import { EmptyStatePanel } from '../../StatePanel'

const getEventName = (eventId: string) => TRACK_EVENTS.find(e => e.id === eventId)?.name ?? eventId

/** Read-only view of the athlete's own profile — deliberately mirrors what a recruiter sees. */
export const AthleteProfileView = () => {
  const navigate = useNavigate()
  const { profile } = useStudentProfile()

  if (profile.isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    )
  }

  if (!profile.data) {
    return (
      <EmptyStatePanel
        title="No profile yet"
        description="Complete onboarding to create your athlete profile."
      />
    )
  }

  const p = profile.data
  const pi = p.personalInfo
  const ai = p.academicInfo
  const ath = p.athleticInfo
  const records = p.personalRecords ?? []

  return (
    <Box>
      <ProfileSection
        title={`${pi.firstName} ${pi.lastName}`}
        icon={
          <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
            <Person />
          </Avatar>
        }
        action={
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate('/profile/edit')}
          >
            Edit profile
          </Button>
        }
      >
        <Typography variant="body1" color="text.secondary" gutterBottom>
          {ai.highSchool}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip label={`Class of ${ai.graduationYear}`} color="primary" />
          <Chip label={`GPA: ${ai.gpa}`} />
          {ai.satScore && <Chip label={`SAT: ${ai.satScore}`} />}
          {ai.actScore && <Chip label={`ACT: ${ai.actScore}`} />}
          <Chip
            label={p.isPublic ? 'Visible to recruiters' : 'Hidden from recruiters'}
            color={p.isPublic ? 'success' : 'default'}
            onClick={() => navigate('/settings')}
          />
        </Box>
        <Box sx={{ maxWidth: 320 }}>
          <Typography variant="caption" color="text.secondary">
            Profile completion — {p.profileCompletion}%
          </Typography>
          <LinearProgress variant="determinate" value={p.profileCompletion} sx={{ mt: 0.5 }} />
        </Box>
      </ProfileSection>

      <Alert severity="info" sx={{ my: 3 }}>
        This is roughly what recruiters see when they search or browse profiles: your city and
        state, not your street address or date of birth.
      </Alert>

      <Stack spacing={3}>
        <ProfileSection title="Personal" icon={<Person />}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ProfileField
                label="City / State"
                value={`${pi.address.city}, ${pi.address.state}`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField label="Email" value={pi.email} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField label="Mobile phone" value={pi.mobilePhone} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField label="Street address" value={pi.address.line1} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField label="ZIP code" value={pi.address.zipCode} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField
                label="Date of birth"
                value={new Date(pi.dateOfBirth).toLocaleDateString()}
              />
            </Grid>
            {pi.bio && (
              <Grid item xs={12}>
                <ProfileField label="Bio" value={pi.bio} />
              </Grid>
            )}
          </Grid>
        </ProfileSection>

        <ProfileSection title="Parent / Guardian">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ProfileField
                label="Name"
                value={`${pi.parentGuardian.firstName} ${pi.parentGuardian.lastName}`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField label="Email" value={pi.parentGuardian.email} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField label="Phone" value={pi.parentGuardian.phoneNumber} />
            </Grid>
          </Grid>
        </ProfileSection>

        <ProfileSection title="Academic" icon={<School />}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <ProfileField label="High school" value={ai.highSchool} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField label="Graduation year" value={ai.graduationYear} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField label="GPA" value={ai.gpa} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <ProfileField label="SAT" value={ai.satScore} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <ProfileField label="ACT" value={ai.actScore} />
            </Grid>
          </Grid>
        </ProfileSection>

        <ProfileSection title="Athletic" icon={<Sports />}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <ProfileField label="Height" value={`${ath.height.feet}'${ath.height.inches}"`} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField
                label="Planned first year of college"
                value={ath.plannedFirstYearOfCollege}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField label="NCAA ID" value={ath.ncaaId} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ProfileField
                label="Club team / position"
                value={[ath.clubTeam, ath.clubPosition].filter(Boolean).join(' — ')}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" gutterBottom>
            Primary events
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
            {ath.primaryEvents.map(eventId => (
              <Chip key={eventId} label={getEventName(eventId)} color="primary" size="small" />
            ))}
          </Box>

          <Typography variant="subtitle2" gutterBottom>
            Personal records
          </Typography>
          {records.length > 0 ? (
            <Grid container spacing={2}>
              {records.map(record => (
                <Grid item xs={6} sm={4} md={3} key={record.id ?? record.eventId}>
                  <ProfileField label={getEventName(record.eventId)} value={record.mark} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No personal records yet.
            </Typography>
          )}

          {ath.recruitingVideoLink && isHttpUrl(ath.recruitingVideoLink) && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<VideoLibrary />}
                href={ath.recruitingVideoLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Watch recruiting video
              </Button>
            </Box>
          )}
        </ProfileSection>
      </Stack>
    </Box>
  )
}
